import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import FunnelLayout from "./FunnelLayout";
import "./CommonNewFunnel.css";

const LS_KEY = "glp1_intake_step8";

// ✅ API-ready question keys
const Q8_KEY =
  "Q8_Do_you_have_any_further_information_which_you_would_like_our_medical_team_to_know";
const Q8A_KEY =
  "Q8a_Provide_details_here_please_do_not_include_urgent_or_emergency_medical_information";

// ✅ FULL answers only
const ANSWERS = {
  YES: "Yes",
  NO: "No",
} as const;

type Q8Answer = (typeof ANSWERS)[keyof typeof ANSWERS] | "";

type FormState = {
  q8: Q8Answer;
  q8a: string; // details text
};

const DEFAULT: FormState = { q8: "", q8a: "" };

type OptionCardProps = {
  name: string;
  label: string;
  checked: boolean;
  onChange: () => void;
};

const OptionCard: React.FC<OptionCardProps> = ({
  name,
  label,
  checked,
  onChange,
}) => {
  return (
    <button
      type="button"
      className={`funnel-option funnel-checkbox ${checked ? "is-selected" : ""}`}
      onClick={onChange}
    >
      <span className={`funnel-control radio ${checked ? "is-selected" : ""}`}>
        <span className="dot" />
      </span>

      <span className="funnel-option__label">{label}</span>

      <input
        className="sr-only"
        type="radio"
        name={name}
        checked={checked}
        onChange={onChange}
      />
    </button>
  );
};

type StoredPayload = {
  [Q8_KEY]?: Q8Answer;
  [Q8A_KEY]?: string;
};

const readFromLS = (): FormState => {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return DEFAULT;

    const parsed = JSON.parse(raw) as StoredPayload;

    const q8Raw = parsed?.[Q8_KEY];
    const q8: Q8Answer = q8Raw === ANSWERS.YES || q8Raw === ANSWERS.NO ? q8Raw : "";

    let q8a = typeof parsed?.[Q8A_KEY] === "string" ? String(parsed[Q8A_KEY]) : "";

    // if q8 is NO, force empty details
    if (q8 === ANSWERS.NO) q8a = "";

    return { q8, q8a };
  } catch {
    return DEFAULT;
  }
};

const buildPayload = (form: FormState): StoredPayload => {
  const payload: StoredPayload = {};
  if (form.q8) payload[Q8_KEY] = form.q8;

  // only store Q8a if Yes (and keep it trimmed)
  if (form.q8 === ANSWERS.YES) {
    const details = form.q8a.trim();
    if (details) payload[Q8A_KEY] = details;
  }

  return payload;
};

const NewFunnelStep8: React.FC = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState<FormState>(DEFAULT);

  // ✅ prevents overwriting LS with DEFAULT on first render
  const [hydrated, setHydrated] = useState(false);

  // ✅ LOAD once (autofill when coming back from step 9)
  useEffect(() => {
    setForm(readFromLS());
    setHydrated(true);
  }, []);

  // ✅ SAVE only after hydration
  useEffect(() => {
    if (!hydrated) return;
    if (!form.q8) return; // don’t save empties

    localStorage.setItem(LS_KEY, JSON.stringify(buildPayload(form)));
  }, [form, hydrated]);

  const showDetails = form.q8 === ANSWERS.YES;

  // ✅ if user switches to No => clear details
  useEffect(() => {
    if (!hydrated) return;
    if (form.q8 === ANSWERS.NO && form.q8a) {
      setForm((p) => ({ ...p, q8a: "" }));
    }
  }, [form.q8, form.q8a, hydrated]);

  const canGoNext =
    form.q8 === ANSWERS.NO ||
    (form.q8 === ANSWERS.YES && form.q8a.trim().length > 0);

  const next = () => {
    if (!canGoNext) return;

    // ✅ force save right before navigation (extra-safe)
    localStorage.setItem(LS_KEY, JSON.stringify(buildPayload(form)));

    navigate("/glp1/intake-form/step-8");
  };

  return (
    <FunnelLayout>
      <h1 className="title-step-1 funnel-q-title-heading" style={{ color: "#000" }}>
        CLAYA medical providers review every form <span>within 24 hours</span>
      </h1>

      <div className="funnel-divider" />

      <div className="funnel-section">
        <div className="funnel-q-title">
          Do you have any further information which you would like our medical team to know?
        </div>

        {([ANSWERS.YES, ANSWERS.NO] as const).map((v) => (
          <OptionCard
            key={v}
            name="q8"
            label={v}
            checked={form.q8 === v}
            onChange={() =>
              setForm((p) => ({
                ...p,
                q8: v,
                // ✅ if switching to No, clear immediately (nice UX)
                q8a: v === ANSWERS.NO ? "" : p.q8a,
              }))
            }
          />
        ))}
      </div>

      {showDetails && (
        <div className="funnel-section">
          <div className="funnel-q-title">
            Provide details here. Please do not include urgent or emergency medical information.
          </div>

          <textarea
            className="funnel-textarea"
            placeholder=""
            value={form.q8a}
            onChange={(e) => setForm((p) => ({ ...p, q8a: e.target.value }))}
            rows={5}
          />
        </div>
      )}

      <button className="next-btn" onClick={next} disabled={!canGoNext}>
        Next →
      </button>
    </FunnelLayout>
  );
};

export default NewFunnelStep8;